import * as React from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Archive,
  Download,
  Edit3,
  Filter,
  ListChecks,
  Plus,
  RefreshCw,
  RotateCcw,
  Upload,
  X,
} from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
} from '@shigo-ui/dashboard-ui';
import { Button, toast } from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import {
  useSupplyAppsMutations,
  useSupplyAppsQuery,
  useSupplyAppStatusCountsQuery,
} from '../../hooks';
import { countActiveSupplyAppsSecondaryFilters } from '../../appsRouteSearch';
import { downloadCsv } from '../../../../../lib/downloadBlob';
import {
  resolveSupplyAppStatusLabel,
  resolveSupplyAppStatusTone,
  SupplyAppsSortDirection,
  SupplyInventoryAppStatus,
  supplyAppsPageSizes,
  type SupplyInventoryApp,
} from '../../model';
import { useAppsRouteFilters } from '../../useAppsRouteFilters';
import { AppsFilters } from '../../AppsFilters';
import { AppsStatusFilter } from '../AppsStatusFilter';
import { AppImportDialog } from '../AppImportDialog/AppImportDialog';
import { PublisherAddAppsDialog } from '../PublisherAppsDialogs';
import { PublisherCsvManageDialog } from '../../../publishers/components/PublisherCsvManageDialog';
import { formatDisplayDate } from '../../../../../lib/format';

enum PublisherAppsDialogType {
  Add = 'ADD',
  Manage = 'MANAGE',
}

export function AppsTable({ publisherId }: { publisherId?: string } = {}) {
  const navigate = useNavigate();
  const { can } = useShigoPermissions();
  const {
    filter,
    setSearch,
    setStatuses,
    setPublishers,
    setPlatforms,
    setIabCategories,
    resetSecondaryFilters,
    setPagination,
    setSort,
  } = useAppsRouteFilters(Boolean(publisherId));
  const scopedFilter = publisherId ? { ...filter, publisherIds: [publisherId] } : filter;
  const query = useSupplyAppsQuery(scopedFilter);
  const counts = useSupplyAppStatusCountsQuery(scopedFilter);
  const mutations = useSupplyAppsMutations();
  const [importOpen, setImportOpen] = React.useState(false);
  const [publisherDialog, setPublisherDialog] = React.useState<PublisherAppsDialogType | null>(
    null,
  );
  const activeFilterCount = countActiveSupplyAppsSecondaryFilters(filter);
  const [filtersOpen, setFiltersOpen] = React.useState(activeFilterCount > 0);
  const [pendingIds, setPendingIds] = React.useState<Set<string>>(() => new Set());
  const search = useDebouncedValue(filter.searchTerm, setSearch, 650);
  const rows = query.data?.content ?? [];

  const setActive = React.useCallback(
    async (app: SupplyInventoryApp) => {
      setPendingIds((current) => new Set(current).add(app.id));
      try {
        if (app.status === SupplyInventoryAppStatus.Active)
          await mutations.archive.mutateAsync(app.id);
        else await mutations.activate.mutateAsync(app.id);
        toast.success(
          dashboardT(
            app.status === SupplyInventoryAppStatus.Active
              ? 'supplyApps.archiveSuccess'
              : 'supplyApps.activateSuccess',
          ),
        );
      } catch (error) {
        showRequestError(error, dashboardT('supplyApps.statusError'));
      } finally {
        setPendingIds((current) => {
          const next = new Set(current);
          next.delete(app.id);
          return next;
        });
      }
    },
    [mutations.activate, mutations.archive],
  );

  const canEdit = can(
    publisherId
      ? ShigoPermission.SupplyPublishersAppsUpdate
      : ShigoPermission.SupplyAppsInventoryEdit,
  );
  const canArchive = can(
    publisherId
      ? ShigoPermission.SupplyPublishersAppsArchive
      : ShigoPermission.SupplyAppsInventoryArchive,
  );
  const canCreate = Boolean(publisherId && can(ShigoPermission.SupplyPublishersAppsCreate));
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<SupplyInventoryApp>([
        {
          id: 'name',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('supplyApps.name'),
          headerClassName: 'w-72 pl-4',
          className: 'w-72 pl-4 font-medium',
          sortable: true,
          sortKey: 'name',
          value: (app) =>
            canEdit ? (
              <Link
                className={dashboardTableTextActionClassName()}
                to="/supply/apps/edit/$appId"
                params={{ appId: app.id }}
              >
                {app.name}
              </Link>
            ) : (
              app.name
            ),
        },
        {
          id: 'status',
          kind: DashboardDataTableColumnKind.Status,
          header: dashboardT('supplyApps.status'),
          headerClassName: 'w-32',
          className: 'w-32',
          sortable: true,
          sortKey: 'status',
          label: (app) => resolveSupplyAppStatusLabel(app.status),
          tone: (app) => resolveSupplyAppStatusTone(app.status),
        },
        {
          id: 'publisher',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('supplyApps.publisher'),
          headerClassName: 'w-56',
          className: 'w-56 truncate',
          value: (app) => app.publisherNames.join(', '),
        },
        {
          id: 'platform',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('supplyApps.platform'),
          headerClassName: 'w-36',
          className: 'w-36',
          value: (app) => app.platform?.name,
        },
        {
          id: 'appStoreId',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('supplyApps.appStoreId'),
          headerClassName: 'w-48',
          className: 'w-48 truncate',
          sortable: true,
          sortKey: 'appStoreId',
          value: (app) => app.appStoreId,
        },
        {
          id: 'bundleId',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('supplyApps.bundleId'),
          headerClassName: 'w-56',
          className: 'w-56 truncate',
          sortable: true,
          sortKey: 'bundleId',
          value: (app) => app.bundleId,
        },
        {
          id: 'created',
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('supplyApps.created'),
          headerClassName: 'w-32',
          className: 'w-32 whitespace-nowrap',
          sortable: true,
          sortKey: 'createdOn',
          value: (app) => formatDisplayDate(app.createdOn),
        },
        {
          id: 'updated',
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('supplyApps.updated'),
          headerClassName: 'w-32',
          className: 'w-32 whitespace-nowrap',
          sortable: true,
          sortKey: 'updatedOn',
          value: (app) => formatDisplayDate(app.updatedOn),
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          cell: (app) =>
            canEdit || canArchive ? (
              <DashboardTableRowActions label={dashboardT('supplyApps.actions')}>
                {canEdit ? (
                  <DashboardTableRowActionItem
                    onSelect={() =>
                      void navigate({ to: '/supply/apps/edit/$appId', params: { appId: app.id } })
                    }
                  >
                    <Edit3 className="h-4 w-4" />
                    {dashboardT('supplyApps.edit')}
                  </DashboardTableRowActionItem>
                ) : null}
                {canArchive ? (
                  <DashboardTableRowActionItem
                    disabled={pendingIds.has(app.id)}
                    onSelect={() => void setActive(app)}
                  >
                    {app.status === SupplyInventoryAppStatus.Active ? (
                      <Archive className="h-4 w-4" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                    {dashboardT(
                      app.status === SupplyInventoryAppStatus.Active
                        ? 'supplyApps.archive'
                        : 'supplyApps.activate',
                    )}
                  </DashboardTableRowActionItem>
                ) : null}
              </DashboardTableRowActions>
            ) : null,
        },
      ]),
    [canArchive, canEdit, navigate, pendingIds, setActive],
  );

  async function exportCsv() {
    try {
      const csv = await mutations.exportApps.mutateAsync(scopedFilter);
      downloadCsv(csv, 'apps.csv');
      toast.success(dashboardT('supplyApps.exportSuccess'));
    } catch (error) {
      showRequestError(error, dashboardT('supplyApps.exportError'));
    }
  }

  return (
    <>
      <DashboardDataTable
        title={dashboardT('supplyApps.title')}
        tableLabel={dashboardT('supplyApps.title')}
        columns={columns}
        items={rows}
        getRowKey={(app) => app.id}
        loading={query.isLoading}
        loadingMessage={dashboardT('supplyApps.loading')}
        errorMessage={query.isError ? dashboardT('supplyApps.loadError') : undefined}
        emptyMessage={dashboardT('supplyApps.empty')}
        minWidthClassName="min-w-[1380px]"
        toolbar={
          <>
            <DashboardTableSearchInput
              aria-label={dashboardT('supplyApps.search')}
              placeholder={dashboardT('supplyApps.search')}
              value={search.draft}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
            />
            <AppsStatusFilter
              statuses={filter.statuses}
              counts={counts.data}
              onStatusesChange={setStatuses}
            />
            {!publisherId ? (
              <div className="flex items-center">
                <Button
                  type="button"
                  variant="outline"
                  className={activeFilterCount ? 'rounded-r-none' : undefined}
                  aria-label={dashboardT('supplyApps.filters')}
                  aria-expanded={filtersOpen}
                  aria-controls="supply-apps-filter-toolbar"
                  onClick={() => {
                    setFiltersOpen((open) => !open);
                  }}
                >
                  <Filter className="h-4 w-4" aria-hidden="true" />
                  {dashboardT('supplyApps.filters')}
                  {activeFilterCount ? (
                    <span className="bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 text-[10px] font-bold">
                      {activeFilterCount}
                    </span>
                  ) : null}
                </Button>
                {activeFilterCount ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive rounded-l-none border-l-0"
                    aria-label={dashboardT('supplyApps.clearFilters')}
                    onClick={resetSecondaryFilters}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </Button>
                ) : null}
              </div>
            ) : null}
            {!publisherId ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label={dashboardT('supplyApps.refresh')}
                onClick={() => {
                  void query.refetch();
                  void counts.refetch();
                }}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            ) : null}
            {!publisherId && can(ShigoPermission.SupplyAppsInventoryExport) ? (
              <Button
                type="button"
                variant="outline"
                disabled={mutations.exportApps.isPending}
                onClick={() => void exportCsv()}
              >
                <Download className="h-4 w-4" />
                {dashboardT('supplyApps.export')}
              </Button>
            ) : null}
            {!publisherId && can(ShigoPermission.SupplyAppsInventoryImport) ? (
              <Button
                type="button"
                onClick={() => {
                  setImportOpen(true);
                }}
              >
                <Upload className="h-4 w-4" />
                {dashboardT('supplyApps.import')}
              </Button>
            ) : null}
            {publisherId && canEdit && rows.length > 0 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPublisherDialog(PublisherAppsDialogType.Manage);
                }}
              >
                <ListChecks className="h-4 w-4" aria-hidden="true" />
                {dashboardT('publisherDetails.apps.manage')}
              </Button>
            ) : null}
            {publisherId && canCreate ? (
              <Button
                type="button"
                onClick={() => {
                  setPublisherDialog(PublisherAppsDialogType.Add);
                }}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {dashboardT('publisherDetails.apps.add')}
              </Button>
            ) : null}
          </>
        }
        filterToolbar={
          filtersOpen ? (
            <div id="supply-apps-filter-toolbar">
              <AppsFilters
                filter={scopedFilter}
                onPublishersChange={publisherId ? undefined : setPublishers}
                onPlatformsChange={setPlatforms}
                onIabCategoriesChange={setIabCategories}
                publisherScoped={Boolean(publisherId)}
              />
            </div>
          ) : undefined
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: supplyAppsPageSizes,
          disabled: query.isFetching,
          onChange: (next) => {
            setPagination(next.page, next.pageSize);
          },
        }}
        sort={{
          value: filter.sortProperty
            ? {
                key: filter.sortProperty,
                direction:
                  filter.sortDirection === SupplyAppsSortDirection.Ascending
                    ? DashboardDataTableSortDirection.Asc
                    : DashboardDataTableSortDirection.Desc,
              }
            : undefined,
          onChange: (next) => {
            if (!next) {
              setSort(null, filter.sortDirection);
              return;
            }
            setSort(
              next.key as typeof filter.sortProperty,
              next.direction === DashboardDataTableSortDirection.Asc
                ? SupplyAppsSortDirection.Ascending
                : SupplyAppsSortDirection.Descending,
            );
          },
        }}
      />
      {importOpen ? (
        <AppImportDialog
          onClose={() => {
            setImportOpen(false);
          }}
        />
      ) : null}
      {publisherId && publisherDialog === PublisherAppsDialogType.Add ? (
        <PublisherAddAppsDialog
          publisherId={publisherId}
          onClose={() => {
            setPublisherDialog(null);
          }}
        />
      ) : null}
      {publisherId && publisherDialog === PublisherAppsDialogType.Manage ? (
        <PublisherCsvManageDialog
          kind="apps"
          filename="publisher-apps.csv"
          downloadPending={mutations.exportPublisherApps.isPending}
          uploadPending={mutations.reimportPublisherApps.isPending}
          onDownload={() => mutations.exportPublisherApps.mutateAsync(publisherId)}
          onUpload={(file) => mutations.reimportPublisherApps.mutateAsync({ publisherId, file })}
          onClose={() => {
            setPublisherDialog(null);
          }}
        />
      ) : null}
    </>
  );
}
